import { useEffect, useMemo, useState } from 'react';
import { Power, Eye, Pencil, Trash2, Plus, Receipt, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { Drawer, DrawerDetailGrid } from '../../../components/ui/Drawer';
import { Modal } from '../../../components/ui/Modal';
import { ConfirmModal } from '../../../components/ui/ConfirmModal';
import { Input, Select, FormField } from '../../../components/ui/Input';
import { Avatar } from '../../../components/ui/Avatar';
import { Badge } from '../../../components/ui/Badge';
import { Table } from '../../../components/ui/Table';
import { syncTimestampColumns } from '../../../components/ui/SyncTimestamps';
import { Tabs } from '../../../components/ui/Tabs';
import api from '../../../services/api';
import {
  fmtDate, fmtAmount, fmtRelative, userInitials,
  INVITE_DEFAULTS, FormFooter, GlobalError, ErrorBanner,
} from '../shared';

const INV_STATUS = {
  pending:    { variant: 'pending',   label: 'En attente', group: 'pending'   },
  paid:       { variant: 'paid',      label: 'Payée',      group: 'paid'      },
  overdue_30: { variant: 'overdue30', label: 'En retard',  group: 'overdue'   },
  overdue_60: { variant: 'overdue60', label: 'En retard',  group: 'overdue'   },
  cancelled:  { variant: 'preparing', label: 'Annulée',    group: 'cancelled' },
};

const ACTION_LABELS = {
  CREATE:           { label: 'Création',       color: 'bg-emerald-100 text-emerald-700' },
  UPDATE:           { label: 'Modification',   color: 'bg-blue-100 text-blue-700'       },
  DELETE:           { label: 'Suppression',    color: 'bg-rose-100 text-rose-700'       },
  INVITE:           { label: 'Invitation',     color: 'bg-purple-100 text-purple-700'   },
  LOGIN:            { label: 'Connexion',      color: 'bg-slate-100 text-slate-600'     },
  LOGOUT:           { label: 'Déconnexion',    color: 'bg-slate-100 text-slate-600'     },
  COMPLETE_SIGNUP:  { label: 'Inscription',    color: 'bg-teal-100 text-teal-700'       },
  IMPERSONATE_START:{ label: 'Vue client',     color: 'bg-amber-100 text-amber-700'     },
  IMPERSONATE_END:  { label: 'Fin vue client', color: 'bg-amber-100 text-amber-700'     },
};

export function ClientDrawer({
  company, onClose, onEdit, onDelete, onToggleStatus, onImpersonate,
  allInvoices, companyStats, ROLE_META, locale, canManage,
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManageUsers = hasPermission('manage_users');
  const [tab, setTab]               = useState('details');
  const [users, setUsers]           = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState(INVITE_DEFAULTS);
  const [inviteErrors, setInviteErrors] = useState({});
  const [inviting, setInviting]     = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [confirm, setConfirm]       = useState(null);
  const [auditLogs, setAuditLogs]   = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  function loadAuditLogs(id) {
    if (!id) return;
    setAuditLoading(true);
    api.get(`/audit-logs/company/${id}?limit=20`)
      .then((r) => setAuditLogs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAuditLogs([]))
      .finally(() => setAuditLoading(false));
  }

  useEffect(() => {
    if (company) {
      setTab('details');
      setUsers([]);
      setUsersError(null);
      setAuditLogs([]);
      loadAuditLogs(company._id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?._id]);

  useEffect(() => {
    if (company && tab === 'users') loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, company?._id]);

  function loadUsers() {
    if (!company?._id) return;
    setUsersLoading(true);
    setUsersError(null);
    api.get(`/users?companyId=${company._id}`)
      .then((r) => setUsers(Array.isArray(r.data?.data) ? r.data.data : []))
      .catch((e) => setUsersError(e.message || t('common.error_occurred')))
      .finally(() => setUsersLoading(false));
  }

  const companyInvoices = useMemo(() => {
    if (!company) return [];
    const cid = String(company._id);
    return allInvoices.filter(
      (inv) => String(inv.clientId?._id ?? inv.clientId ?? '') === cid,
    );
  }, [allInvoices, company]);

  const stats = company ? (companyStats.get(String(company._id)) ?? null) : null;

  function openAddUser() {
    setInviteData({ email: '', role: Object.keys(ROLE_META)[0] || '' });
    setInviteErrors({});
    setInviteSent(false);
    setInviteModal(true);
  }

  async function handleInvite() {
    if (!canManageUsers) return;
    const errs = {};
    if (!inviteData.email.trim()) errs.email = t('common.required_field');
    setInviteErrors(errs);
    if (Object.keys(errs).length) return;
    setInviting(true);
    try {
      await api.post('/users/invite', { ...inviteData, companyId: company._id });
      setInviteSent(true);
      loadUsers();
    } catch (e) {
      setInviteErrors({
        _global: e.response?.data?.message || e.message || t('common.error_occurred'),
      });
    } finally {
      setInviting(false);
    }
  }

  async function handleDeleteUser() {
    if (!confirm || !canManageUsers) return;
    setSaving(true);
    try {
      await api.delete(`/users/${confirm._id}`);
      setConfirm(null);
      loadUsers();
    } catch {
      // stay open on error
    } finally {
      setSaving(false);
    }
  }

  const userColumns = [
    {
      key: 'fullname',
      label: t('users.col_user'),
      render: (_, row) => (
        <div className="flex items-center gap-2.5">
          <Avatar initials={userInitials(row)} size="sm" />
          <div>
            <p className="font-medium text-slate-800">{row.fullname || row.email}</p>
            {row.title && <p className="text-[11px] text-slate-400">{row.title}</p>}
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      label: t('users.col_email'),
      render: (v) => <span className="text-slate-500 text-[12px]">{v || '—'}</span>,
    },
    {
      key: 'role',
      label: t('users.col_role'),
      render: (v) => {
        const roleName = v?.name ?? v;
        return roleName ? (
          <Badge variant={ROLE_META[roleName]?.variant || 'info'}>
            {ROLE_META[roleName]?.label || roleName}
          </Badge>
        ) : <span className="text-slate-400">—</span>;
      },
    },
    {
      key: 'invitationStatus',
      label: t('users.col_status'),
      render: (v, row) => {
        if (v === 'pending')  return <Badge variant="warning">{t('users.status_pending')}</Badge>;
        if (v === 'accepted') return <Badge variant="success">{t('users.status_accepted')}</Badge>;
        return (
          <Badge variant={row.isActive ? 'success' : 'preparing'}>
            {row.isActive ? t('common.active') : t('common.inactive')}
          </Badge>
        );
      },
    },
    {
      key: '_act',
      label: '',
      align: 'right',
      render: (_, row) => canManageUsers ? (
        <button
          onClick={(e) => { e.stopPropagation(); setConfirm(row); }}
          title={t('common.delete')}
          className="w-7 h-7 grid place-items-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition"
        >
          <Trash2 size={12} strokeWidth={1.75} />
        </button>
      ) : null,
    },
  ];

  const invoiceColumns = [
    {
      key: 'reference',
      label: 'Référence',
      render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
    },
    {
      key: 'status',
      label: 'Statut',
      render: (v) => {
        const s = INV_STATUS[v] || { variant: 'info', label: v || '—' };
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
    },
    {
      key: 'issuedAt',
      label: 'Émission',
      render: (v) => <span className="text-slate-500 text-[12px]">{fmtDate(v, locale)}</span>,
    },
    {
      key: 'dueDate',
      label: 'Échéance',
      render: (v) => <span className="text-slate-500 text-[12px]">{fmtDate(v, locale)}</span>,
    },
    {
      key: 'ageDays',
      label: 'Retard',
      align: 'right',
      render: (v, row) => {
        const grp = INV_STATUS[row.status]?.group;
        if (!v || grp !== 'overdue') return <span className="text-slate-300">—</span>;
        return <span className="font-semibold text-[12px] text-rose-500 tabular-nums">+{v} j</span>;
      },
    },
    {
      key: 'amountTTC',
      label: 'Montant',
      align: 'right',
      render: (v, row) => (
        <span className="tabular-nums font-semibold text-slate-800 text-[12.5px]">
          {fmtAmount(v ?? row.amountEur, row.fretCurrencyCode || 'EUR', locale)}
        </span>
      ),
    },
    ...syncTimestampColumns(t, locale),
  ];

  const detailFields = company ? [
    { label: t('companies.detail_erp'),     value: company.erpCode  || '—', mono: true },
    { label: t('companies.detail_ice'),     value: company.ice      || '—', mono: true },
    { label: t('companies.detail_address'), value: company.adresse  || '—'             },
    { label: t('companies.detail_created'), value: fmtDate(company.createdAt, locale)  },
    { label: 'Mis à jour',                  value: fmtDate(company.updatedAt, locale)  },
  ] : [];

  const usersTabLabel    = tab === 'users' && users.length > 0
    ? `${t('companies.tab_users')} (${users.length})` : t('companies.tab_users');
  const invoicesTabLabel = companyInvoices.length > 0
    ? `Factures (${companyInvoices.length})` : 'Factures';

  return (
    <>
      <Drawer
        open={!!company}
        onClose={onClose}
        title={company?.name}
        subtitle={t('companies.subtitle_client')}
        width="w-[720px]"
        headerRight={
          canManage && (
          <div className="flex items-center gap-1.5 mr-1">
            {company && (
              <>
                <button
                  onClick={async () => { await onToggleStatus(company); loadAuditLogs(company._id); }}
                  title={company.isActive ? 'Cliquer pour désactiver' : 'Cliquer pour activer'}
                  className={`h-7 px-2.5 flex items-center gap-1.5 rounded-md text-[11.5px] font-medium border transition ${
                    company.isActive
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                      : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  <Power size={10} strokeWidth={2} />
                  {company.isActive ? t('common.active') : t('common.inactive')}
                </button>
                <button
                  onClick={() => onImpersonate(company._id)}
                  title="Voir le portail en tant que ce client"
                  className="h-7 px-2.5 flex items-center gap-1.5 rounded-md text-[11.5px] font-medium border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition"
                >
                  <Eye size={11} strokeWidth={2} />
                  Vue client
                </button>
              </>
            )}
            <button
              onClick={onEdit}
              title={t('common.edit')}
              className="w-8 h-8 grid place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
            >
              <Pencil size={13} strokeWidth={1.75} />
            </button>
            <button
              onClick={onDelete}
              title={t('common.delete')}
              className="w-8 h-8 grid place-items-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition"
            >
              <Trash2 size={13} strokeWidth={1.75} />
            </button>
          </div>
          )
        }
      >
        <div className="-m-5 flex flex-col min-h-full">
          <div className="px-5 border-b border-slate-200 bg-white flex-shrink-0">
            <Tabs
              tabs={[
                { key: 'details',  label: t('companies.tab_details') },
                { key: 'invoices', label: invoicesTabLabel            },
                { key: 'users',    label: usersTabLabel               },
              ]}
              value={tab}
              onChange={setTab}
            />
          </div>

          <div className="p-5 flex flex-col gap-5 flex-1 overflow-y-auto">
            {tab === 'details' && (
              <>
                <DrawerDetailGrid fields={detailFields} />
                <div className="mt-1">
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-3">
                    Activité récente
                  </p>
                  {auditLoading ? (
                    <p className="text-[12px] text-slate-400">Chargement…</p>
                  ) : auditLogs.length === 0 ? (
                    <p className="text-[12px] text-slate-400">Aucune activité enregistrée.</p>
                  ) : (
                    <div className="flex flex-col gap-0">
                      {auditLogs.map((log, i) => {
                        const meta = ACTION_LABELS[log.action] ?? { label: log.action, color: 'bg-slate-100 text-slate-600' };
                        return (
                          <div key={log._id ?? i} className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
                            <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap flex-shrink-0 ${meta.color}`}>
                              {meta.label}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] text-slate-700 leading-snug">
                                <span className="font-medium">{log.resource}</span>
                                {log.performedByName ? (
                                  <span className="text-slate-400"> · {log.performedByName}</span>
                                ) : log.performedByEmail ? (
                                  <span className="text-slate-400"> · {log.performedByEmail}</span>
                                ) : null}
                              </p>
                              {!log.success && (
                                <p className="text-[11px] text-rose-500 mt-0.5">{log.errorMessage || 'Échec'}</p>
                              )}
                            </div>
                            <span className="text-[11px] text-slate-400 whitespace-nowrap flex-shrink-0 mt-0.5">
                              {fmtRelative(log.createdAt)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {tab === 'invoices' && (
              companyInvoices.length === 0 ? (
                <div className="py-12 flex flex-col items-center gap-3 text-slate-400">
                  <Receipt size={28} strokeWidth={1.25} className="opacity-40" />
                  <p className="text-[13px]">Aucune facture pour ce client</p>
                </div>
              ) : (
                <>
                  {stats && (
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { label: 'Total',      value: stats.count,        sub: fmtAmount(stats.total,       'EUR', locale), color: 'text-slate-800',  bg: 'bg-slate-200'  },
                        { label: 'Payées',     value: stats.paidCount,    sub: fmtAmount(stats.paidAmt,     'EUR', locale), color: 'text-emerald-700', bg: 'bg-emerald-50' },
                        { label: 'En attente', value: stats.pendingCount, sub: '—',                                         color: 'text-amber-700',  bg: 'bg-amber-50'   },
                        { label: 'En retard',  value: stats.overdueCount, sub: fmtAmount(stats.overdueAmt,  'EUR', locale), color: 'text-rose-600',   bg: 'bg-rose-50'    },
                      ].map((k) => (
                        <div key={k.label} className={`${k.bg} rounded-lg p-3`}>
                          <p className="text-[10px] text-slate-500 mb-1">{k.label}</p>
                          <p className={`text-[18px] font-bold leading-none ${k.color}`}>{k.value}</p>
                          <p className="text-[9.5px] text-slate-400 font-mono mt-1 truncate">{k.sub}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <Table columns={invoiceColumns} rows={companyInvoices} rowKey="_id" emptyState={null} />
                </>
              )
            )}

            {tab === 'users' && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-[12px] text-slate-500">
                    {usersLoading
                      ? t('common.loading')
                      : `${users.length} ${t('users.col_user').toLowerCase()}${users.length !== 1 ? 's' : ''}`}
                  </p>
                  {canManageUsers && (
                    <button
                      onClick={openAddUser}
                      className="h-8 px-3 flex items-center gap-1.5 rounded-lg bg-primary text-white text-[12px] font-medium hover:opacity-90 transition"
                    >
                      <Plus size={13} strokeWidth={2} />
                      {t('companies.invite_user')}
                    </button>
                  )}
                </div>
                {usersError && <ErrorBanner message={usersError} onRetry={loadUsers} />}
                <Table
                  columns={userColumns}
                  rows={users}
                  loading={usersLoading}
                  rowKey="_id"
                  emptyState={
                    <div className="py-12 flex flex-col items-center gap-3 text-slate-400">
                      <Users size={28} strokeWidth={1.25} className="opacity-40" />
                      <p className="text-[13px]">{t('companies.users_empty')}</p>
                    </div>
                  }
                />
              </>
            )}
          </div>
        </div>
      </Drawer>

      <Modal
        open={inviteModal}
        onClose={() => setInviteModal(false)}
        title={t('users.invite_title')}
        size="sm"
        footer={
          inviteSent ? (
            <button
              onClick={() => setInviteModal(false)}
              className="h-9 px-4 rounded-lg bg-primary text-white text-[13px] font-semibold hover:opacity-90 transition"
            >
              {t('common.close')}
            </button>
          ) : (
            <FormFooter onCancel={() => setInviteModal(false)} onSave={handleInvite} saving={inviting} />
          )
        }
      >
        {inviteSent ? (
          <div className="py-4 text-center flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-emerald-50 grid place-items-center">
              <span className="text-emerald-500 text-xl">✓</span>
            </div>
            <p className="text-[13px] text-slate-700 font-medium">
              {t('users.invite_sent')} <strong>{inviteData.email}</strong>
            </p>
            <p className="text-[12px] text-slate-400">{t('users.invite_sent_detail')}</p>
          </div>
        ) : (
          <>
            {inviteErrors._global && <GlobalError message={inviteErrors._global} />}
            <div className="grid gap-4">
              <FormField
                label={t('users.invite_email')}
                required
                helper={inviteErrors.email}
                error={!!inviteErrors.email}
              >
                <Input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => {
                    setInviteData((d) => ({ ...d, email: e.target.value }));
                    setInviteErrors((e2) => ({ ...e2, email: undefined }));
                  }}
                  placeholder={t('users.invite_email_placeholder')}
                  state={inviteErrors.email ? 'error' : 'default'}
                />
              </FormField>
              <FormField label={t('users.invite_role')}>
                <Select
                  value={inviteData.role}
                  onChange={(e) => setInviteData((d) => ({ ...d, role: e.target.value }))}
                >
                  {Object.entries(ROLE_META).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </Select>
              </FormField>
            </div>
          </>
        )}
      </Modal>

      <ConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleDeleteUser}
        loading={saving}
        title={t('users.delete_title', { name: confirm?.fullname })}
        message={t('users.delete_message')}
        confirmLabel={t('users.delete_confirm')}
      />
    </>
  );
}
